import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Badge,
} from 'reactstrap';
import api from '../../services/api';

const TeamGroupingModal = ({ isOpen, toggle, league, onGroupsUpdated }) => {
  const [teams, setTeams] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [saving, setSaving] = useState(false);
  const [schedules, setSchedules] = useState([]);

  const fetchTeams = useCallback(async () => {
    try {
      const [teamsResponse, schedulesResponse] = await Promise.all([
        api.get(`/league-teams/?league=${league.id}`),
        api.get(`/league-schedules/?league=${league.id}`)
      ]);
      const teamsData = teamsResponse.data;
      setTeams(teamsData);
      setSchedules(schedulesResponse.data);
      
      // Parse existing groups from teams
      const existingGroups = new Map();
      teamsData.forEach(team => {
        if (team.group) {
          if (!existingGroups.has(team.group)) {
            existingGroups.set(team.group, []);
          }
          existingGroups.get(team.group).push(team);
        }
      });
      
      setGroups(Array.from(existingGroups.entries()).map(([groupId, teamsList]) => ({
        id: groupId,
        teams: teamsList,
      })));
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  }, [league?.id]);

  useEffect(() => {
    if (isOpen && league?.id) {
      fetchTeams();
    }
  }, [isOpen, league?.id, fetchTeams]);

  const handleTeamSelect = (team) => {
    setSelectedTeams(prev => {
      const isSelected = prev.find(t => t.id === team.id);
      if (isSelected) {
        return prev.filter(t => t.id !== team.id);
      } else {
        // Check if team has same schedule as already selected teams
        if (prev.length > 0 && prev[0].schedule !== team.schedule) {
          alert('Teams must have the same schedule to be grouped together');
          return prev;
        }
        return [...prev, team];
      }
    });
  };

  const handleCreateGroup = () => {
    if (selectedTeams.length < 2) {
      alert('Please select at least 2 teams to create a group');
      return;
    }

    // Create group ID from team IDs
    const groupId = selectedTeams
      .map(t => t.team)
      .sort((a, b) => a - b)
      .join('-');

    // Check if group already exists
    const existingGroup = groups.find(g => g.id === groupId);
    if (existingGroup) {
      alert('This group already exists');
      return;
    }

    setGroups(prev => [...prev, {
      id: groupId,
      teams: [...selectedTeams],
    }]);

    setSelectedTeams([]);
  };

  const handleRemoveGroup = (groupId) => {
    setGroups(prev => prev.filter(g => g.id !== groupId));
  };

  const handleRemoveTeamFromGroup = (groupId, teamId) => {
    setGroups(prev => prev.map(g => {
      if (g.id === groupId) {
        const updatedTeams = g.teams.filter(t => t.id !== teamId);
        // If only one team left, remove the group
        if (updatedTeams.length < 2) {
          return null;
        }
        
        // Recalculate group ID
        const newGroupId = updatedTeams
          .map(t => t.team)
          .sort((a, b) => a - b)
          .join('-');
        
        return {
          id: newGroupId,
          teams: updatedTeams,
        };
      }
      return g;
    }).filter(Boolean));
  };

  const handleAddTeamToGroup = (groupId, team) => {
    // Check if team has same schedule as group teams
    const group = groups.find(g => g.id === groupId);
    if (group && group.teams.length > 0 && group.teams[0].schedule !== team.schedule) {
      alert('Teams must have the same schedule to be grouped together');
      return;
    }
    
    setGroups(prev => prev.map(g => {
      if (g.id === groupId) {
        const updatedTeams = [...g.teams, team];
        
        // Recalculate group ID with the new team
        const newGroupId = updatedTeams
          .map(t => t.team)
          .sort((a, b) => a - b)
          .join('-');
        
        return {
          id: newGroupId,
          teams: updatedTeams,
        };
      }
      return g;
    }));
  };

  const handleSaveGroups = async () => {
    setSaving(true);
    try {
      // Create a map of team IDs to their group
      const teamGroupMap = new Map();
      
      groups.forEach(group => {
        group.teams.forEach(team => {
          teamGroupMap.set(team.id, group.id);
        });
      });

      // Update all teams
      const updatePromises = teams.map(team => {
        const groupId = teamGroupMap.get(team.id) || null;
        
        // Only update if the group has changed
        if (team.group !== groupId) {
          return api.put(`/league-teams/${team.id}/`, {
            league: team.league,
            team: team.team,
            schedule: team.schedule,
            group: groupId,
          });
        }
        return Promise.resolve();
      });

      await Promise.all(updatePromises);
      
      if (onGroupsUpdated) {
        onGroupsUpdated();
      }
      
      toggle();
    } catch (error) {
      console.error('Error saving groups:', error);
      alert('Failed to save groups. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getScheduleName = (scheduleId) => {
    if (!scheduleId) return 'No Schedule';
    const schedule = schedules.find(s => s.id === scheduleId);
    return schedule?.title || `Schedule ${scheduleId}`;
  };

  const getUnassignedTeams = () => {
    const assignedTeamIds = new Set();
    groups.forEach(group => {
      group.teams.forEach(team => assignedTeamIds.add(team.id));
    });
    
    return teams.filter(team => !assignedTeamIds.has(team.id));
  };

  const getUnassignedTeamsBySchedule = () => {
    const unassigned = getUnassignedTeams();
    const bySchedule = new Map();
    
    unassigned.forEach(team => {
      const scheduleKey = team.schedule || 'no-schedule';
      if (!bySchedule.has(scheduleKey)) {
        bySchedule.set(scheduleKey, []);
      }
      bySchedule.get(scheduleKey).push(team);
    });
    
    return Array.from(bySchedule.entries()).map(([scheduleId, teamsList]) => ({
      scheduleId: scheduleId === 'no-schedule' ? null : scheduleId,
      scheduleName: getScheduleName(scheduleId === 'no-schedule' ? null : parseInt(scheduleId)),
      teams: teamsList,
    }));
  };

  const getGroupsBySchedule = () => {
    const bySchedule = new Map();
    
    groups.forEach(group => {
      if (group.teams.length > 0) {
        const scheduleKey = group.teams[0].schedule || 'no-schedule';
        if (!bySchedule.has(scheduleKey)) {
          bySchedule.set(scheduleKey, []);
        }
        bySchedule.get(scheduleKey).push(group);
      }
    });
    
    return Array.from(bySchedule.entries()).map(([scheduleId, groupsList]) => ({
      scheduleId: scheduleId === 'no-schedule' ? null : scheduleId,
      scheduleName: getScheduleName(scheduleId === 'no-schedule' ? null : parseInt(scheduleId)),
      groups: groupsList,
    }));
  };

  const unassignedTeams = getUnassignedTeams();
  const unassignedBySchedule = getUnassignedTeamsBySchedule();
  const groupsBySchedule = getGroupsBySchedule();

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl">
      <ModalHeader toggle={toggle}>
        Team Grouping - {league?.name}
      </ModalHeader>
      <ModalBody>
        <Row>
          <Col md="6">
            <h6 className="mb-3">Unassigned Teams</h6>
            {unassignedTeams.length === 0 ? (
              <Card className="mb-3">
                <CardBody>
                  <p className="text-muted mb-0">All teams are assigned to groups</p>
                </CardBody>
              </Card>
            ) : (
              <div className="mb-3">
                {unassignedBySchedule.map(({ scheduleId, scheduleName, teams: scheduleTeams }) => (
                  <Card key={scheduleId || 'no-schedule'} className="mb-2">
                    <CardHeader className="bg-light py-2">
                      <small className="fw-semibold">{scheduleName}</small>
                    </CardHeader>
                    <CardBody>
                      <div className="d-flex flex-wrap gap-2">
                        {scheduleTeams.map(team => (
                          <Badge
                            key={team.id}
                            color={selectedTeams.find(t => t.id === team.id) ? 'primary' : 'secondary'}
                            pill
                            style={{ cursor: 'pointer', fontSize: '0.9rem', padding: '0.5rem 0.75rem' }}
                            onClick={() => handleTeamSelect(team)}
                          >
                            {team.team_display}
                          </Badge>
                        ))}
                      </div>
                    </CardBody>
                  </Card>
                ))}
              </div>
            )}

            <div className="d-flex align-items-center gap-2 mb-3">
              <Button
                color="success"
                size="sm"
                onClick={handleCreateGroup}
                disabled={selectedTeams.length < 2}
              >
                Create Group ({selectedTeams.length} selected)
              </Button>
              {selectedTeams.length > 0 && (
                <Button
                  color="secondary"
                  size="sm"
                  outline
                  onClick={() => setSelectedTeams([])}
                >
                  Clear Selection
                </Button>
              )}
            </div>

            <small className="text-muted">
              Select 2 or more teams with the same schedule and click "Create Group" to schedule them together.
            </small>
          </Col>

          <Col md="6">
            <h6 className="mb-3">Existing Groups</h6>
            {groups.length === 0 ? (
              <Card>
                <CardBody>
                  <p className="text-muted mb-0">No groups created yet</p>
                </CardBody>
              </Card>
            ) : (
              <div className="d-flex flex-column gap-3">
                {groupsBySchedule.map(({ scheduleId, scheduleName, groups: scheduleGroups }) => (
                  <div key={scheduleId || 'no-schedule'}>
                    <h6 className="small fw-semibold text-muted mb-2">{scheduleName}</h6>
                    {scheduleGroups.map(group => {
                      // Get unassigned teams with the same schedule as this group
                      const groupSchedule = group.teams.length > 0 ? group.teams[0].schedule : null;
                      const compatibleUnassigned = unassignedTeams.filter(t => t.schedule === groupSchedule);
                      
                      return (
                        <Card key={group.id} className="mb-2">
                          <CardHeader className="d-flex justify-content-between align-items-center bg-light py-2">
                            <span className="fw-semibold small">Group: {group.id}</span>
                            <Button
                              color="danger"
                              size="sm"
                              outline
                              onClick={() => handleRemoveGroup(group.id)}
                            >
                              Remove Group
                            </Button>
                          </CardHeader>
                          <CardBody>
                            <div className="d-flex flex-wrap gap-2 mb-3">
                              {group.teams.map(team => (
                                <Badge
                                  key={team.id}
                                  color="primary"
                                  pill
                                  style={{ fontSize: '0.9rem', padding: '0.5rem 0.75rem' }}
                                >
                                  {team.team_display}
                                  <span
                                    style={{ 
                                      marginLeft: '0.5rem', 
                                      cursor: 'pointer',
                                      fontWeight: 'bold'
                                    }}
                                    onClick={() => handleRemoveTeamFromGroup(group.id, team.id)}
                                  >
                                    ×
                                  </span>
                                </Badge>
                              ))}
                            </div>
                            {compatibleUnassigned.length > 0 && (
                              <div className="border-top pt-2">
                                <small className="text-muted d-block mb-2">Add team to this group:</small>
                                <div className="d-flex flex-wrap gap-2">
                                  {compatibleUnassigned.map(team => (
                                    <Badge
                                      key={team.id}
                                      color="secondary"
                                      outline
                                      pill
                                      style={{ cursor: 'pointer', fontSize: '0.85rem', padding: '0.4rem 0.6rem' }}
                                      onClick={() => handleAddTeamToGroup(group.id, team)}
                                    >
                                      + {team.team_display}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                          </CardBody>
                        </Card>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="primary" onClick={handleSaveGroups} disabled={saving}>
          {saving ? 'Saving...' : 'Save Groups'}
        </Button>{' '}
        <Button color="secondary" onClick={toggle}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default TeamGroupingModal;
